// ==========================================================
// Viewport Transform Engine (Precision Zoom & Pan Controller)
// ==========================================================

export class ViewportController {
  private stageEl: HTMLElement | null = null;
  private wrapperEl: HTMLElement | null = null;

  private scale: number = 1.0;
  private panX: number = 0;
  private panY: number = 0;

  private minScale: number = 1.0;
  private maxScale: number = 3.5;

  private isHandMode: boolean = false;

  private onTransformChange?: (scale: number, panX: number, panY: number) => void;

  constructor() {
    this.stageEl = document.getElementById('canvas-stage');
    this.wrapperEl = document.getElementById('atelier-canvas-wrapper');
  }

  public setCallbacks(onChange: (scale: number, panX: number, panY: number) => void) {
    this.onTransformChange = onChange;
  }

  public getScale(): number {
    return this.scale;
  }

  public getPan(): { x: number; y: number } {
    return { x: this.panX, y: this.panY };
  }

  public isHandModeActive(): boolean {
    return this.isHandMode;
  }

  public setHandMode(active: boolean) {
    this.isHandMode = active;
    if (this.stageEl) {
      if (active) {
        this.stageEl.classList.add('hand-mode');
      } else {
        this.stageEl.classList.remove('hand-mode');
      }
    }
  }

  public toggleHandMode(): boolean {
    this.setHandMode(!this.isHandMode);
    return this.isHandMode;
  }

  /**
   * Zoom by delta at a specific center anchor point (screen coords)
   */
  public zoomAt(deltaScale: number, anchorClientX?: number, anchorClientY?: number) {
    const prevScale = this.scale;
    const nextScale = Math.max(this.minScale, Math.min(this.maxScale, this.scale + deltaScale));

    if (nextScale === prevScale) return;

    if (!this.stageEl || !this.wrapperEl || anchorClientX === undefined || anchorClientY === undefined) {
      this.scale = nextScale;
      this.clampPan();
      this.applyTransform();
      return;
    }

    const wrapperRect = this.wrapperEl.getBoundingClientRect();
    const anchorX = anchorClientX - wrapperRect.left - wrapperRect.width / 2;
    const anchorY = anchorClientY - wrapperRect.top - wrapperRect.height / 2;

    // Adjust pan so the point under cursor remains stationary
    const ratio = nextScale / prevScale;
    this.panX = anchorX - (anchorX - this.panX) * ratio;
    this.panY = anchorY - (anchorY - this.panY) * ratio;

    this.scale = nextScale;
    this.clampPan();
    this.applyTransform();
  }

  /**
   * Pan offset by delta dx, dy
   */
  public panBy(dx: number, dy: number) {
    this.panX += dx;
    this.panY += dy;
    this.clampPan();
    this.applyTransform();
  }

  /**
   * Reset zoom and pan to 100% centered default
   */
  public resetView() {
    this.scale = 1.0;
    this.panX = 0;
    this.panY = 0;
    this.applyTransform();
  }

  private clampPan() {
    if (!this.stageEl || !this.wrapperEl) return;

    if (this.scale <= 1.0) {
      this.panX = 0;
      this.panY = 0;
      return;
    }

    const stageRect = this.stageEl.getBoundingClientRect();
    const stageWidth = stageRect.width / this.scale;
    const stageHeight = stageRect.height / this.scale;

    // Allow panning within reasonable bounds so canvas doesn't fly offscreen
    const maxPanX = (stageWidth * (this.scale - 1)) / 2 + 100;
    const maxPanY = (stageHeight * (this.scale - 1)) / 2 + 100;

    this.panX = Math.max(-maxPanX, Math.min(maxPanX, this.panX));
    this.panY = Math.max(-maxPanY, Math.min(maxPanY, this.panY));
  }

  public applyTransform() {
    if (!this.stageEl) return;
    this.stageEl.style.transform = `translate3d(${this.panX}px, ${this.panY}px, 0) scale(${this.scale})`;
    if (this.onTransformChange) {
      this.onTransformChange(this.scale, this.panX, this.panY);
    }
  }

  /**
   * Convert Screen (clientX, clientY) to Canvas Logical Coordinates (0..logicalWidth, 0..logicalHeight)
   */
  public screenToCanvas(
    clientX: number,
    clientY: number,
    logicalWidth: number,
    logicalHeight: number
  ): { x: number; y: number } {
    if (!this.stageEl) return { x: 0, y: 0 };

    const rect = this.stageEl.getBoundingClientRect();

    // The rect width & height already account for scale
    const normX = (clientX - rect.left) / rect.width;
    const normY = (clientY - rect.top) / rect.height;

    return {
      x: normX * logicalWidth,
      y: normY * logicalHeight
    };
  }
}
